import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { TrackBadge } from '@/ui/TrackBadge';
import { ParticipantLoginForm } from '@/ui/ParticipantLoginForm';

export function MyPlanPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
  });

  const bookingsQuery = useQuery({
    queryKey: ['my-bookings'],
    queryFn: api.myBookings,
    enabled: meQuery.isSuccess,
  });

  const logoutMutation = useMutation({
    mutationFn: api.participantLogout,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['me'] }),
        queryClient.invalidateQueries({ queryKey: ['agenda'] }),
        queryClient.invalidateQueries({ queryKey: ['my-bookings'] }),
      ]);
      navigate('/login');
    },
  });

  if (meQuery.error instanceof ApiError && meQuery.error.status === 401) {
    return (
      <section className="panel">
        <ParticipantLoginForm
          redirectTo="/my-plan"
          title="Logge dich ein, um deinen Plan zu sehen"
          description="Sobald du eingeloggt bist, siehst du hier direkt alle gebuchten Workshops und kannst deinen Plan als PDF drucken."
        />
      </section>
    );
  }

  return (
    <div className="stack stack--xl">
      <section className="panel panel--accent">
        <div className="panel__header">
          <span className="eyebrow">Meine Auswahl</span>
          <h2>{meQuery.data?.attendee.display_name}</h2>
          <p>Hier siehst du alle aktuell gebuchten Sessions. Änderungen machst du direkt in der Agenda.</p>
        </div>
        <div className="inline-actions">
          <button className="button button--ghost" type="button" onClick={() => window.open('/my-plan/print', '_blank', 'noopener')}>
            PDF / Drucken
          </button>
          <button className="button button--ghost" onClick={() => logoutMutation.mutate()} disabled={logoutMutation.isPending}>
            {logoutMutation.isPending ? 'Log-out…' : 'Log-out'}
          </button>
        </div>
      </section>

      {bookingsQuery.isLoading ? <p className="notice">Dein Plan wird geladen…</p> : null}

      {bookingsQuery.data?.bookings.length === 0 ? (
        <section className="panel">
          <p className="notice">Noch keine Workshops gebucht. In der Agenda kannst du pro Slot eine Session auswählen.</p>
        </section>
      ) : null}

      {bookingsQuery.data?.bookings.map((booking) => (
        <section className="panel" key={booking.bookingId}>
          <div className="session-card__topline">
            <TrackBadge track={booking.track} />
            <span className="session-card__format">{booking.formatLabel}</span>
          </div>
          <h3>{booking.title}</h3>
          {booking.subtitle ? <p className="session-card__subtitle">{booking.subtitle}</p> : null}
          <p className="plan-time">{booking.startTime} – {booking.endTime}</p>
          <dl className="session-card__meta">
            <div>
              <dt>Referentin</dt>
              <dd>{booking.speaker ?? 'Wird ergänzt'}</dd>
            </div>
            <div>
              <dt>Raum</dt>
              <dd className="room-label">{booking.roomName}</dd>
            </div>
          </dl>
        </section>
      ))}
    </div>
  );
}
